import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Square, MessageSquare, Paperclip, X, Loader2 } from 'lucide-react';
import { MessageBubble } from './MessageBubble.jsx';

/**
 * ChatPane — 工作區中欄對話流
 *
 * 接受外部已 useSSEChat 過的 chat 物件（讓 Workspace 集中管 SSE 狀態，
 * 左右欄也能用 sendMessage / isStreaming）。
 */
export function ChatPane({ chat, avatarName, emptyTitle, emptyHint, placeholder }) {
    const { t } = useTranslation('workspace');
    const { messages, isStreaming, sendMessage, abort, error, hasMore, loadOlder, loadingOlder } = chat;
    const [input, setInput] = useState('');
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    // 往上滑載入更舊對話時，記下載入前的 scrollHeight → prepend 後還原視窗位置（不被拉到底）
    const prependingRef = useRef(false);
    const prevScrollHeightRef = useRef(0);

    // scroll 行為：prepend 更舊訊息 → 還原原本閱讀位置；其餘（新訊息 / streaming）→ 貼底
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (prependingRef.current) {
            el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
            prependingRef.current = false;
        } else {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages.length, messages[messages.length - 1]?.text]);

    // 往上滑到頂 → 載入更舊對話。記錄現在 scrollHeight 供 prepend 後還原位置。
    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el || !hasMore || loadingOlder) return;
        if (el.scrollTop < 60) {
            prevScrollHeightRef.current = el.scrollHeight;
            prependingRef.current = true;
            loadOlder?.();
        }
    };

    const addFiles = (incoming) => {
        if (!incoming?.length) return;
        const arr = Array.from(incoming);
        setFiles((prev) => {
            const next = [...prev];
            for (const f of arr) {
                if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
            }
            return next;
        });
    };
    const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

    const handleSend = () => {
        const text = input.trim();
        if (!text && files.length === 0) return;
        if (files.length > 0) {
            // 帶檔案 — reuse 既有 modal 的 onApply pattern: (text, { files, displayText })
            const fParts = files.map((f) => `  ${files.indexOf(f) + 1}. ${f.name} (${Math.round(f.size / 1024)} KB)`).join('\n');
            const fullText = text
                ? `${text}\n\n${t('chat.attachmentsLabel')}\n${fParts}`
                : `${t('chat.attachmentsPrompt')}\n${fParts}`;
            const displayText = text || t('chat.attachmentsCount', { n: files.length });
            sendMessage(fullText, { files, displayText });
        } else {
            sendMessage(text);
        }
        setInput('');
        setFiles([]);
    };
    const handleKeyDown = (e) => {
        // ⚠️ IME composition(注音、日文、韓文輸入)按 Enter 是「確認選字」,不是送出。
        // e.nativeEvent.isComposing 為 true 期間要忽略 Enter。keyCode 229 是另一個 IME signal。
        if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(e.dataTransfer?.files);
    };

    return (
        <div
            className="flex flex-col h-full min-h-0 relative"
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {dragOver && (
                <div className="absolute inset-0 z-10 bg-accent-50/80 backdrop-blur-sm border-2 border-dashed border-accent-500 rounded flex items-center justify-center pointer-events-none">
                    <div className="text-accent-700 text-sm font-medium inline-flex items-center gap-2">
                        <Paperclip size={16} /> {t('chat.dropToUpload')}
                    </div>
                </div>
            )}
            <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-6 py-4">
                {/* 頂部「更舊對話」載入區 — 往上滑自動觸發，也提供按鈕 fallback（內容短於視窗時 scroll 不會觸發）*/}
                {hasMore && (
                    <div className="flex justify-center py-2">
                        {loadingOlder ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-ink-400">
                                <Loader2 size={12} className="animate-spin" /> {t('chat.loadingOlder')}
                            </span>
                        ) : (
                            <button
                                onClick={() => { const el = scrollRef.current; if (el) { prevScrollHeightRef.current = el.scrollHeight; prependingRef.current = true; } loadOlder?.(); }}
                                className="text-xs px-3 py-1 rounded-full border border-cream-300 text-ink-500 hover:bg-cream-100 transition"
                            >
                                {t('chat.loadOlder')}
                            </button>
                        )}
                    </div>
                )}
                {messages.length === 0 && !isStreaming && (
                    <div className="h-full flex flex-col items-center justify-center text-ink-400 text-sm">
                        <MessageSquare size={36} className="mb-3 text-ink-300" strokeWidth={1.4} />
                        <div className="text-base text-ink-700 mb-1">{emptyTitle || t('chat.emptyTitle')}</div>
                        <div className="text-xs max-w-sm text-center">
                            {emptyHint != null
                                ? emptyHint
                                : <>{avatarName ? t('chat.emptyTarget', { name: avatarName }) : ''}<br />{t('chat.emptyHint')}</>}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <MessageBubble key={m.id || i} message={m} />
                ))}
                {error && (
                    <div className="text-xs text-danger-500 italic my-2">{t('chat.error', { error: error.message })}</div>
                )}
            </div>

            <div className="border-t border-cream-200 px-3 py-3 bg-cream-50/50">
                {/* 已選檔案 chips */}
                {files.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {files.map((f, i) => (
                            <div key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-cream-300 rounded text-[11px] text-ink-700">
                                <Paperclip size={10} strokeWidth={1.6} />
                                <span className="truncate max-w-[140px]">{f.name}</span>
                                <span className="text-ink-400">{Math.round(f.size / 1024)}KB</span>
                                <button
                                    onClick={() => removeFile(i)}
                                    disabled={isStreaming}
                                    className="text-ink-400 hover:text-danger-500 disabled:opacity-40"
                                >
                                    <X size={10} strokeWidth={2} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-end gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        accept=".xlsx,.xls,.csv,.tsv,.txt,.docx,.pdf,.md,.png,.jpg,.jpeg,.heic"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isStreaming}
                        title={t('chat.attachTitle')}
                        className="size-10 flex items-center justify-center rounded-md text-ink-500 hover:bg-cream-100 hover:text-ink-900 transition shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Paperclip size={16} />
                    </button>
                    <textarea
                        rows={2}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isStreaming ? t('chat.inputPlaceholderStreaming') : (placeholder || t('chat.inputPlaceholder'))}
                        disabled={isStreaming}
                        className="flex-1 resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white disabled:bg-cream-100 disabled:text-ink-400"
                    />
                    {isStreaming ? (
                        <button
                            onClick={abort}
                            title={t('chat.abort')}
                            className="size-10 flex items-center justify-center rounded-md bg-danger-500 hover:bg-danger-500/90 text-white transition shrink-0"
                        >
                            <Square size={16} fill="currentColor" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSend}
                            disabled={!input.trim() && files.length === 0}
                            title={t('chat.send')}
                            className="size-10 flex items-center justify-center rounded-md bg-ink-900 hover:bg-ink-700 text-white transition shrink-0 disabled:bg-cream-300 disabled:cursor-not-allowed"
                        >
                            <Send size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
